import * as React from 'react'
import { Link as RouterLink, useLocation } from 'react-router'

/** Own scheme (`https:`, `mailto:`, `tel:`) or protocol-relative — either way it leaves this app. */
const LEAVES_THE_APP = /^([a-z][a-z0-9+.-]*:|\/\/)/i

export type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement>

/**
 * Anchor that resolves an in-app destination through the router — a history
 * push instead of a full document load. External URLs, `mailto:`/`tel:` and
 * new-tab links stay plain anchors, as do fragments on the current page: those
 * scroll rather than navigate (see `scrollToFragment`).
 */
export const Anchor = React.forwardRef<HTMLAnchorElement, AnchorProps>(function Anchor(
  { href, target, onClick, ...rest },
  ref
) {
  if (href !== undefined && target !== '_blank' && href.startsWith('#')) {
    return <a ref={ref} href={href} onClick={fragmentClickHandler(href, onClick)} {...rest} />
  }
  if (href === undefined || target === '_blank' || LEAVES_THE_APP.test(href)) {
    return <a ref={ref} href={href} target={target} onClick={onClick} {...rest} />
  }
  return <InAppAnchor ref={ref} href={href} target={target} onClick={onClick} {...rest} />
})

const InAppAnchor = React.forwardRef<HTMLAnchorElement, AnchorProps & { href: string }>(function InAppAnchor(
  { href, onClick, ...rest },
  ref
) {
  const { pathname } = useLocation()
  const destination = new URL(href, `http://localhost${pathname}`)
  // A bare `#id` resolves against the live URL, so the current query string survives the jump.
  if (destination.hash && destination.pathname === pathname) {
    const hash = destination.hash
    return <a ref={ref} href={hash} onClick={fragmentClickHandler(hash, onClick)} {...rest} />
  }
  return <RouterLink ref={ref} to={href} onClick={onClick} {...rest} />
})

/**
 * Scrolls to a fragment on the current page instead of letting the browser jump to
 * it. Deliberately kept out of the router: `<ScrollRestoration>` snaps to the hash
 * in a layout effect, which would cut the animation short.
 */
function fragmentClickHandler(hash: string, onClick: AnchorProps['onClick']): AnchorProps['onClick'] {
  return (event) => {
    onClick?.(event)
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const target = document.getElementById(decodeURIComponent(hash.slice(1)))
    if (!target) return

    event.preventDefault()
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' })
    // Same entry, not a new one: the hash marks where you are, it isn't a page to go back from.
    window.history.replaceState(window.history.state, '', hash)
  }
}
